import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Mail, Calendar, Youtube, MessageSquare, Check, ChevronRight, 
  ArrowRight, Sparkles, Loader2, X, Building2, UtensilsCrossed, ShoppingCart,
  Clapperboard, Briefcase, Stars
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/UserAuthContext';
import { useUserId } from '../context/UserAuthContext';
import { usePageVisibility } from '../context/PageVisibilityContext';
import { ALL_PAGES } from '../config/pages';

const API_URL = window.location.origin;

const INDUSTRY_ICONS = {
  Building2, UtensilsCrossed, ShoppingCart, Clapperboard, Briefcase, Sparkles
};

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { user, completeOnboarding, isAuthenticated } = useAuth();
  const userId = useUserId();
  const { setHiddenPagesExternal } = usePageVisibility();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [connectedServices, setConnectedServices] = useState({
    email: false,
    calendar: false,
    youtube: false
  });
  const [industryTemplates, setIndustryTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedSubCompany, setSelectedSubCompany] = useState(null);
  const [selectedSubCompanies, setSelectedSubCompanies] = useState([]); // For Enterprise multi-select
  const [selectedRole, setSelectedRole] = useState(null);
  
  // Check if selected template is Enterprise (allows multi-select)
  const isEnterprise = selectedTemplate?.category === 'Enterprise';

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
    }
  }, [isAuthenticated, navigate]);

  // Redirect if already completed onboarding
  useEffect(() => {
    if (user?.onboarding_completed) {
      const isAdmin = user.is_admin === true || user.is_admin === 'true';
      navigate(isAdmin ? '/admin' : '/dashboard');
    }
  }, [user, navigate]);

  // Redirect to pricing if no subscription plan
  useEffect(() => {
    // Give a moment for user data to load
    const timer = setTimeout(() => {
      if (user && !user.subscription_plan) {
        console.log('[Onboarding] No subscription plan found, redirecting to pricing');
        toast.info('Please select a plan first');
        navigate('/pricing');
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [user, navigate]);

  // Map subscription plan to template category
  const getTemplateCategory = () => {
    const plan = user?.subscription_plan;
    console.log('[Onboarding] User subscription_plan:', plan);
    if (plan === 'personal') return 'Personal';
    if (plan === 'smallbiz') return 'Small Business';
    if (plan === 'enterprise') return 'Enterprise';
    // Default to Personal if no plan (fallback)
    return 'Personal';
  };
  
  const templateCategory = getTemplateCategory();

  // Fetch industry templates (use axios to avoid rrweb fetch interception)
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setIndustryTemplates([]); // Clear while loading
        const { data } = await axios.get(`${API_URL}/api/industry-templates`);
        // Filter templates by user's subscription plan category
        const allTemplates = data.templates || [];
        const filteredTemplates = allTemplates.filter(t => t.category === templateCategory);
        console.log(`[Onboarding] Filtering for category: ${templateCategory}`);
        console.log(`[Onboarding] All templates: ${allTemplates.length}, Filtered: ${filteredTemplates.length}`);
        console.log(`[Onboarding] Filtered template names:`, filteredTemplates.map(t => t.name));
        setIndustryTemplates(filteredTemplates);
      } catch (e) { console.error('[Onboarding] Failed to fetch industry templates:', e); }
    };
    if (user) {
      fetchTemplates();
    }
  }, [user, templateCategory]);

  const handleSelectIndustry = (template) => {
    setSelectedTemplate(template);
    setSelectedSubCompany(null);
    setSelectedSubCompanies([]); // Reset multi-select
    setSelectedRole(null);
  };
  
  // Toggle sub-company selection for Enterprise (multi-select)
  const handleToggleSubCompany = (sc) => {
    if (!isEnterprise) {
      // Small Business: single select
      setSelectedSubCompany(sc);
      return;
    }
    // Enterprise: multi-select
    setSelectedSubCompanies(prev => {
      const exists = prev.find(s => s.id === sc.id);
      if (exists) {
        return prev.filter(s => s.id !== sc.id);
      }
      return [...prev, sc];
    });
  };
  
  // Check if a sub-company is selected (works for both single and multi)
  const isSubCompanySelected = (sc) => {
    if (isEnterprise) {
      return selectedSubCompanies.some(s => s.id === sc.id);
    }
    return selectedSubCompany?.id === sc.id;
  };

  // Apply page visibility based on a list of core pages
  const applyPageSettings = async (corePages) => {
    if (!userId) return;
    try {
      const corePaths = new Set(corePages || ['/']);
      const storageKey = `operatr_hidden_pages_${userId}`;
      const pagesToHide = ALL_PAGES
        .filter(p => !p.alwaysOn && !corePaths.has(p.path))
        .map(p => p.path);
      localStorage.setItem(storageKey, JSON.stringify(pagesToHide));
      setHiddenPagesExternal(pagesToHide);
      await fetch(`${API_URL}/api/user-addons/page-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, hidden_pages: pagesToHide })
      });
    } catch (e) { console.error('[Onboarding] Failed to save page settings:', e); }
  };

  // Called from the Role step — applies role pages (or template defaults) then advances
  const handleRoleContinue = async () => {
    if (selectedTemplate) {
      setLoading(true);
      try {
        // Priority: selected role > first role in sub-company > template defaults > safe fallback
        let corePages;
        if (selectedRole) {
          corePages = selectedRole.core_pages;
        } else if (isEnterprise && selectedSubCompanies.length > 0) {
          // Enterprise: merge core pages from all selected sub-companies
          const allPages = new Set();
          selectedSubCompanies.forEach(sc => {
            (sc.roles || []).forEach(role => {
              (role.core_pages || []).forEach(p => allPages.add(p));
            });
          });
          corePages = allPages.size > 0 ? Array.from(allPages) : ['/', '/tasks', '/calendar', '/communications-re'];
        } else if (selectedSubCompany?.roles?.length) {
          corePages = selectedSubCompany.roles[0].core_pages;
        } else if (selectedTemplate.core_pages?.length) {
          corePages = selectedTemplate.core_pages;
        } else {
          corePages = ['/', '/tasks', '/calendar', '/communications-re'];
        }
        await applyPageSettings(corePages);
        
        // Build request payload
        const payload = { 
          user_id: userId, 
          template_id: selectedTemplate.id,
          role_id: selectedRole?.id
        };
        
        // Enterprise: pass array of sub-companies
        if (isEnterprise && selectedSubCompanies.length > 0) {
          payload.sub_companies = selectedSubCompanies.map(sc => sc.id);
        } else if (selectedSubCompany) {
          payload.sub_company = selectedSubCompany.id;
        }
        
        // Link the template to the user account
        await fetch(`${API_URL}/api/user-addons/apply-template`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } catch (e) { /* non-critical */ }
      finally { setLoading(false); }
    }
    handleNext();
  };

  const steps = [
    {
      id: 'welcome',
      title: 'Welcome to OPERATR',
      subtitle: "Your personal operating system for managing everything in one place",
      icon: Sparkles
    },
    {
      id: 'industry',
      title: `Choose Your ${templateCategory} Industry`,
      subtitle: templateCategory === 'Personal' 
        ? 'Select a category that best fits your personal workflow'
        : templateCategory === 'Small Business'
        ? 'Select the industry that matches your business'
        : 'Select the enterprise sector you operate in',
      icon: Building2
    },
    {
      id: 'role',
      title: selectedTemplate?.sub_companies?.length > 0
        ? (isEnterprise 
            ? (selectedSubCompanies.length > 0 ? `Selected ${selectedSubCompanies.length} Companies` : 'Select Your Companies')
            : (selectedSubCompany ? 'Select Your Role' : 'Select Company Type'))
        : 'Select Your Role',
      subtitle: selectedTemplate?.sub_companies?.length > 0
        ? (isEnterprise
            ? (selectedSubCompanies.length > 0 
                ? `Managing: ${selectedSubCompanies.map(s => s.name).join(', ')}`
                : `Select multiple company types to oversee with ${selectedTemplate?.name}`)
            : (selectedSubCompany
                ? `Fine-tune your workspace for ${selectedSubCompany.name}`
                : `Choose the type of ${selectedTemplate?.name} you work in`))
        : 'Fine-tune your workspace for how you actually work',
      icon: Stars
    },
    {
      id: 'email',
      title: 'Connect Your Email',
      subtitle: 'Manage all your emails from Gmail, Outlook, and more in one inbox',
      icon: Mail,
      service: 'email'
    },
    {
      id: 'calendar',
      title: 'Sync Your Calendar',
      subtitle: 'Keep track of all your events and meetings across platforms',
      icon: Calendar,
      service: 'calendar'
    },
    {
      id: 'youtube',
      title: 'Connect YouTube',
      subtitle: 'Manage your subscriptions and playlists',
      icon: Youtube,
      service: 'youtube'
    },
    {
      id: 'complete',
      title: "You're All Set!",
      subtitle: 'Start exploring your personal command center',
      icon: Check
    }
  ];

  const handleConnectService = async (service) => {
    setLoading(true);
    
    try {
      if (service === 'email') {
        // Redirect to Nylas connection
        const response = await fetch(`${API_URL}/api/nylas/auth/connect?user_id=${user?.user_id || user?.email}`);
        const data = await response.json();
        if (data.auth_url) {
          // Store that we're in onboarding flow
          localStorage.setItem('onboarding_return', 'true');
          window.location.href = data.auth_url;
          return;
        }
      } else if (service === 'calendar') {
        // Calendar is connected via email
        setConnectedServices(prev => ({ ...prev, calendar: true }));
        toast.success('Calendar synced successfully!');
      } else if (service === 'youtube') {
        // YouTube just needs API key which is pre-configured
        setConnectedServices(prev => ({ ...prev, youtube: true }));
        toast.success('YouTube connected!');
      }
    } catch (error) {
      toast.error(`Failed to connect ${service}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    setCurrentStep(prev => prev + 1);
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      await completeOnboarding();
      toast.success('Welcome to OPERATR!');
      const isAdmin = user?.is_admin === true || user?.is_admin === 'true';
      navigate(isAdmin ? '/admin' : '/dashboard');
    } catch (error) {
      toast.error('Failed to complete onboarding');
    } finally {
      setLoading(false);
    }
  };

  const currentStepData = steps[currentStep];
  const isServiceStep = currentStepData?.service;
  const isConnected = isServiceStep && connectedServices[currentStepData.service];

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between mb-2">
            {steps.map((step, idx) => (
              <div
                key={step.id}
                className={`flex-1 h-1 mx-1 rounded-full transition-all ${
                  idx <= currentStep ? 'bg-white' : 'bg-gray-800'
                }`}
              />
            ))}
          </div>
          <p className="text-gray-500 text-sm text-center">
            Step {currentStep + 1} of {steps.length}
          </p>
        </div>

        {/* Main Card */}
        <div className="bg-black border border-white/30 rounded-2xl p-8 text-center transition-all duration-300 hover:border-white/60 hover:shadow-[0_0_28px_rgba(255,255,255,0.18)]">
          {/* Icon */}
          <div className={`w-20 h-20 mx-auto rounded-2xl flex items-center justify-center mb-6 ${
            currentStep === steps.length - 1 
              ? 'bg-green-500/20' 
              : 'bg-gradient-to-br from-purple-500/20 to-pink-500/20'
          }`}>
            {React.createElement(currentStepData.icon, {
              size: 40,
              className: currentStep === steps.length - 1 ? 'text-green-400' : 'text-purple-400'
            })}
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-white mb-3">
            {currentStepData.title}
          </h1>
          <p className="text-gray-400 mb-8 max-w-md mx-auto">
            {currentStepData.subtitle}
          </p>

          {/* Welcome Step */}
          {currentStep === 0 && (
            <div className="space-y-4">
              <p className="text-gray-300">
                Hi {user?.name?.split(' ')[0] || 'there'}! Let's get your workspace set up.
              </p>
              <p className="text-gray-500 text-sm">
                You can connect your accounts now or skip and do it later.
              </p>
              <button
                onClick={handleNext}
                className="w-full py-3 bg-white hover:bg-gray-100 text-black font-semibold rounded-lg transition-all flex items-center justify-center gap-2 border border-white hover:shadow-[0_0_22px_rgba(255,255,255,0.35)]"
              >
                Get Started
                <ArrowRight size={18} />
              </button>
            </div>
          )}

          {/* Industry Selection Step */}
          {currentStepData?.id === 'industry' && (
            <div className="space-y-4 text-left">
              {/* Plan tier badge */}
              <div className="flex justify-center mb-2">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                  templateCategory === 'Personal' ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30' :
                  templateCategory === 'Small Business' ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' :
                  'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                }`}>
                  <Sparkles size={12} />
                  {templateCategory} Plan
                </span>
              </div>
              {industryTemplates.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                  <Loader2 size={24} className="animate-spin mb-2" />
                  <p className="text-sm">Loading {templateCategory.toLowerCase()} templates...</p>
                </div>
              ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {industryTemplates.map(tmpl => {
                  const IconComp = INDUSTRY_ICONS[tmpl.icon] || Briefcase;
                  const isSelected = selectedTemplate?.id === tmpl.id;
                  return (
                    <button
                      key={tmpl.id}
                      onClick={() => handleSelectIndustry(tmpl)}
                      data-testid={`industry-${tmpl.id}`}
                      className={`p-4 rounded-xl border text-left transition-all ${
                        isSelected
                          ? 'border-purple-500/60 bg-purple-500/15 ring-1 ring-purple-500/40'
                          : 'border-gray-700 bg-gray-800/50 hover:border-gray-600 hover:bg-gray-800'
                      }`}
                    >
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-2.5" style={{ backgroundColor: `${tmpl.color}20` }}>
                        <IconComp size={18} style={{ color: tmpl.color }} />
                      </div>
                      <p className={`font-semibold text-sm ${isSelected ? 'text-white' : 'text-gray-200'}`}>{tmpl.name}</p>
                      <p className="text-gray-500 text-xs mt-0.5 leading-snug line-clamp-2">{tmpl.description}</p>
                      {isSelected && <span className="inline-flex items-center gap-1 mt-2 text-xs text-purple-400 font-medium"><Check size={10} /> Selected</span>}
                    </button>
                  );
                })}
              </div>
              )}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleNext}
                  disabled={!selectedTemplate}
                  data-testid="industry-continue-btn"
                  className="flex-1 py-3 bg-white hover:bg-gray-100 text-black font-semibold rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-40 border border-white hover:shadow-[0_0_22px_rgba(255,255,255,0.35)]"
                >
                  Continue <ArrowRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* Role Selection Step */}
          {currentStepData?.id === 'role' && (
            <div className="space-y-4 text-left">
              {selectedTemplate ? (
                // Small Business / Enterprise: 2-phase (Sub-Company → Role)
                selectedTemplate.sub_companies?.length > 0 ? (
                  // Enterprise multi-select: Don't go to phase 2, show continue after selections
                  isEnterprise ? (
                    <>
                      <p className="text-gray-400 text-sm text-center">
                        Select multiple company types to manage within{' '}
                        <span className="text-white font-medium">{selectedTemplate.name}</span>
                        <br />
                        <span className="text-purple-400/70 text-xs">(Enterprise allows managing multiple companies)</span>
                      </p>
                      <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                        {selectedTemplate.sub_companies.map(sc => {
                          const isSel = isSubCompanySelected(sc);
                          return (
                            <button
                              key={sc.id}
                              onClick={() => handleToggleSubCompany(sc)}
                              data-testid={`subcompany-${sc.id}`}
                              className={`w-full p-4 rounded-xl border text-left transition-all ${
                                isSel
                                  ? 'border-purple-500/60 bg-purple-500/15 ring-1 ring-purple-500/40'
                                  : 'border-gray-700 bg-gray-800/50 hover:border-purple-500/50 hover:bg-purple-500/10'
                              }`}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <p className={`font-semibold text-sm ${isSel ? 'text-white' : 'text-gray-200'}`}>{sc.name}</p>
                                  {sc.description && (
                                    <p className="text-gray-500 text-xs mt-1 leading-snug">{sc.description}</p>
                                  )}
                                  <p className="text-gray-600 text-xs mt-2">{sc.roles?.length || 0} roles available</p>
                                </div>
                                {isSel && <Check size={16} className="text-purple-400 mt-0.5 flex-shrink-0" />}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                      {selectedSubCompanies.length > 0 && (
                        <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
                          <p className="text-purple-300 text-xs font-medium mb-1">Selected Companies ({selectedSubCompanies.length}):</p>
                          <p className="text-purple-200/70 text-xs">{selectedSubCompanies.map(s => s.name).join(', ')}</p>
                        </div>
                      )}
                      <div className="flex gap-3 pt-2">
                        <button
                          onClick={handleRoleContinue}
                          data-testid="subcompany-skip-btn"
                          className="flex-1 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg text-sm transition-colors"
                        >
                          Use defaults
                        </button>
                        <button
                          onClick={handleRoleContinue}
                          disabled={selectedSubCompanies.length === 0 || loading}
                          data-testid="enterprise-continue-btn"
                          className="flex-1 py-3 bg-white hover:bg-gray-100 text-black font-semibold rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-40 border border-white hover:shadow-[0_0_22px_rgba(255,255,255,0.35)]"
                        >
                          {loading ? (
                            <><Loader2 size={16} className="animate-spin" /> Applying...</>
                          ) : (
                            <>Continue with {selectedSubCompanies.length} {selectedSubCompanies.length === 1 ? 'company' : 'companies'} <ArrowRight size={16} /></>
                          )}
                        </button>
                      </div>
                    </>
                  ) : !selectedSubCompany ? (
                    // Small Business Phase 1: Select Sub-Company (single)
                    <>
                      <p className="text-gray-400 text-sm text-center">
                        Choose a company type within{' '}
                        <span className="text-white font-medium">{selectedTemplate.name}</span>
                      </p>
                      <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                        {selectedTemplate.sub_companies.map(sc => (
                          <button
                            key={sc.id}
                            onClick={() => setSelectedSubCompany(sc)}
                            data-testid={`subcompany-${sc.id}`}
                            className="w-full p-4 rounded-xl border border-gray-700 bg-gray-800/50 hover:border-purple-500/50 hover:bg-purple-500/10 text-left transition-all"
                          >
                            <p className="font-semibold text-sm text-gray-200">{sc.name}</p>
                            {sc.description && (
                              <p className="text-gray-500 text-xs mt-1 leading-snug">{sc.description}</p>
                            )}
                            <p className="text-gray-600 text-xs mt-2">{sc.roles?.length || 0} roles available</p>
                          </button>
                        ))}
                      </div>
                      <div className="flex gap-3 pt-2">
                        <button
                          onClick={handleRoleContinue}
                          data-testid="subcompany-skip-btn"
                          className="flex-1 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg text-sm transition-colors"
                        >
                          Use defaults
                        </button>
                      </div>
                    </>
                  ) : (
                    // Phase 2: Select Role within Sub-Company
                    <>
                      <div className="flex items-center gap-2 text-sm">
                        <button
                          onClick={() => { setSelectedSubCompany(null); setSelectedRole(null); }}
                          className="text-gray-500 hover:text-gray-300 text-xs underline transition-colors"
                          data-testid="back-to-subcompany-btn"
                        >
                          ← {selectedTemplate.name}
                        </button>
                        <span className="text-gray-600">/</span>
                        <span className="text-white font-medium text-xs">{selectedSubCompany.name}</span>
                      </div>
                      {(selectedSubCompany.roles || []).length > 0 ? (
                        <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                          {selectedSubCompany.roles.map(role => {
                            const isSel = selectedRole?.id === role.id;
                            return (
                              <button
                                key={role.id}
                                onClick={() => setSelectedRole(isSel ? null : role)}
                                data-testid={`role-${role.id}`}
                                className={`w-full p-4 rounded-xl border text-left transition-all ${
                                  isSel
                                    ? 'border-purple-500/60 bg-purple-500/15 ring-1 ring-purple-500/40'
                                    : 'border-gray-700 bg-gray-800/50 hover:border-gray-600 hover:bg-gray-800'
                                }`}
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <div className="flex-1 min-w-0">
                                    <p className={`font-semibold text-sm ${isSel ? 'text-white' : 'text-gray-200'}`}>{role.name}</p>
                                    {role.description && (
                                      <p className="text-gray-500 text-xs mt-1 leading-snug">{role.description}</p>
                                    )}
                                    <p className="text-gray-600 text-xs mt-2">{role.core_pages?.length || 0} core pages</p>
                                  </div>
                                  {isSel && <Check size={16} className="text-purple-400 mt-0.5 flex-shrink-0" />}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-sm text-center py-4">No roles defined for this company type.</p>
                      )}
                      <div className="flex gap-3 pt-2">
                        <button
                          onClick={handleRoleContinue}
                          data-testid="role-skip-btn"
                          className="flex-1 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg text-sm transition-colors"
                        >
                          Use defaults
                        </button>
                        <button
                          onClick={handleRoleContinue}
                          disabled={!selectedRole || loading}
                          data-testid="role-continue-btn"
                          className="flex-1 py-3 bg-white hover:bg-gray-100 text-black font-semibold rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-40 border border-white hover:shadow-[0_0_22px_rgba(255,255,255,0.35)]"
                        >
                          {loading ? (
                            <><Loader2 size={16} className="animate-spin" /> Applying...</>
                          ) : (
                            <>Continue <ArrowRight size={16} /></>
                          )}
                        </button>
                      </div>
                    </>
                  )
                ) : selectedTemplate.roles?.length > 0 ? (
                  // Personal: direct role selection
                  <>
                    <p className="text-gray-400 text-sm text-center">
                      Choose your role within{' '}
                      <span className="text-white font-medium">{selectedTemplate.name}</span>
                    </p>
                    <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                      {selectedTemplate.roles.map(role => {
                        const isSel = selectedRole?.id === role.id;
                        return (
                          <button
                            key={role.id}
                            onClick={() => setSelectedRole(isSel ? null : role)}
                            data-testid={`role-${role.id}`}
                            className={`w-full p-4 rounded-xl border text-left transition-all ${
                              isSel
                                ? 'border-purple-500/60 bg-purple-500/15 ring-1 ring-purple-500/40'
                                : 'border-gray-700 bg-gray-800/50 hover:border-gray-600 hover:bg-gray-800'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <p className={`font-semibold text-sm ${isSel ? 'text-white' : 'text-gray-200'}`}>{role.name}</p>
                                {role.description && (
                                  <p className="text-gray-500 text-xs mt-1 leading-snug">{role.description}</p>
                                )}
                                <p className="text-gray-600 text-xs mt-2">{role.core_pages?.length || 0} core pages</p>
                              </div>
                              {isSel && <Check size={16} className="text-purple-400 mt-0.5 flex-shrink-0" />}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    <div className="flex gap-3 pt-2">
                      <button
                        onClick={handleRoleContinue}
                        data-testid="role-skip-btn"
                        className="flex-1 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg text-sm transition-colors"
                      >
                        Use defaults
                      </button>
                      <button
                        onClick={handleRoleContinue}
                        disabled={!selectedRole || loading}
                        data-testid="role-continue-btn"
                        className="flex-1 py-3 bg-white hover:bg-gray-100 text-black font-semibold rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-40 border border-white hover:shadow-[0_0_22px_rgba(255,255,255,0.35)]"
                      >
                        {loading ? (
                          <><Loader2 size={16} className="animate-spin" /> Applying...</>
                        ) : (
                          <>Continue <ArrowRight size={16} /></>
                        )}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="text-center space-y-4 py-4">
                    <p className="text-gray-500 text-sm">No roles defined for this template.</p>
                    <button
                      onClick={handleNext}
                      data-testid="role-no-roles-continue-btn"
                      className="w-full py-3 bg-white hover:bg-gray-100 text-black font-semibold rounded-lg flex items-center justify-center gap-2 border border-white hover:shadow-[0_0_22px_rgba(255,255,255,0.35)]"
                    >
                      Continue <ArrowRight size={16} />
                    </button>
                  </div>
                )
              ) : (
                <div className="text-center space-y-4 py-4">
                  <p className="text-gray-500 text-sm">No industry selected — you can set this up later.</p>
                  <button
                    onClick={handleNext}
                    data-testid="role-no-roles-continue-btn"
                    className="w-full py-3 bg-white hover:bg-gray-100 text-black font-semibold rounded-lg flex items-center justify-center gap-2 border border-white hover:shadow-[0_0_22px_rgba(255,255,255,0.35)]"
                  >
                    Continue <ArrowRight size={16} />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Service Connection Steps */}
          {isServiceStep && (
            <div className="space-y-4">
              {isConnected ? (
                <div className="bg-green-500/20 border border-green-500/30 rounded-xl p-4 flex items-center justify-center gap-3">
                  <Check className="text-green-400" size={24} />
                  <span className="text-green-400 font-medium">Connected!</span>
                </div>
              ) : (
                <button
                  onClick={() => handleConnectService(currentStepData.service)}
                  disabled={loading}
                  className="w-full py-3 bg-white hover:bg-gray-100 text-black font-semibold rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 border border-white hover:shadow-[0_0_22px_rgba(255,255,255,0.35)]"
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      Connect {currentStepData.service === 'email' ? 'Email' : currentStepData.service === 'calendar' ? 'Calendar' : 'YouTube'}
                      <ChevronRight size={18} />
                    </>
                  )}
                </button>
              )}

              <button
                onClick={handleSkip}
                className="w-full py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 font-medium rounded-lg transition-colors"
              >
                {isConnected ? 'Continue' : 'Skip for now'}
              </button>
            </div>
          )}

          {/* Complete Step */}
          {currentStep === steps.length - 1 && (
            <div className="space-y-4">
              {/* Connected Services Summary */}
              <div className="bg-black border border-white/20 rounded-xl p-4 text-left transition-all hover:border-white/40 hover:shadow-[0_0_18px_rgba(255,255,255,0.12)]">
                <p className="text-gray-400 text-sm mb-3">Connected services:</p>
                <div className="space-y-2">
                  {Object.entries(connectedServices).map(([service, connected]) => (
                    <div key={service} className="flex items-center justify-between">
                      <span className="text-gray-300 capitalize">{service}</span>
                      {connected ? (
                        <Check size={18} className="text-green-400" />
                      ) : (
                        <X size={18} className="text-gray-600" />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <p className="text-gray-500 text-sm">
                You can always connect more services from Settings.
              </p>

              <button
                onClick={handleComplete}
                disabled={loading}
                className="w-full py-3 bg-white hover:bg-gray-100 text-black font-semibold rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 border border-white hover:shadow-[0_0_22px_rgba(255,255,255,0.35)]"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Finishing...
                  </>
                ) : (
                  <>
                    Go to Dashboard
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Skip All — removed per design request */}
      </div>
    </div>
  );
};

export default OnboardingPage;
